/**
 * Zustand store for in-app notifications.
 *
 * Fetches from /api/notifications, subscribes to realtime for instant delivery.
 * Provides unread count for badge, list for dropdown, mark-read actions.
 */
import { create } from "zustand";
import { apiClient } from "./api-client";
import { supabase } from "./supabase";
import { leaveRealtimeChannel, uniqueChannelTopic } from "./realtime";

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string | null;
  action_url: string | null;
  actor_id: string | null;
  metadata: Record<string, any> | null;
  is_read: boolean;
  created_at: string;
}

// Reset generation (sign-out / account switch) — the same rule as license-requests.ts. `reset()`
// runs from `resetAllStores` when one account signs out or another signs in, but a read, a revert
// or a realtime event of the PREVIOUS account can still land after the wipe and put that account's
// notices (request and track names) back in the next account's bell. Each records this number when
// it starts and drops its write if a reset has happened since.
let resetGeneration = 0;

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  loadedOnce: boolean;
  /**
   * The last list read failed. The tray says so (with a retry) instead of "No unread
   * notifications", which it said while the badge beside it still counted the unread ones.
   */
  loadFailed: boolean;

  // Actions
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  addNotification: (notification: Notification) => void;
  subscribeToRealtime: (userId: string) => () => void;
  /** Wipe every notice on sign-out / account switch (called by resetAllStores). */
  reset: () => void;
}

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  loadedOnce: false,
  loadFailed: false,

  fetchNotifications: async () => {
    const generation = resetGeneration;
    set({ loading: true, loadFailed: false });
    try {
      const result = await apiClient.get<{ items: Notification[]; total: number }>(
        "/api/notifications?limit=30&unread_only=true"
      );
      if (generation !== resetGeneration) return;
      set({
        notifications: result.items,
        loading: false,
        loadedOnce: true,
      });
    } catch {
      if (generation !== resetGeneration) return;
      set({ loading: false, loadFailed: true });
    }
  },

  fetchUnreadCount: async () => {
    const generation = resetGeneration;
    try {
      const result = await apiClient.get<{ count: number }>(
        "/api/notifications/unread-count"
      );
      if (generation !== resetGeneration) return;
      set({ unreadCount: result.count });
    } catch {
      // silent
    }
  },

  markAsRead: async (id: string) => {
    // Optimistic — remove from list immediately
    const generation = resetGeneration;
    const prev = get().notifications;
    const wasUnread = prev.some((n) => n.id === id && !n.is_read);
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
      unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
    }));
    try {
      await apiClient.patch(`/api/notifications/${id}/read`);
    } catch {
      // Revert on failure — never into another account's bell.
      if (generation !== resetGeneration) return;
      set({ notifications: prev, unreadCount: wasUnread ? get().unreadCount + 1 : get().unreadCount });
    }
  },

  markAllAsRead: async () => {
    // Optimistic — clear entire list
    const generation = resetGeneration;
    const prev = get().notifications;
    const prevCount = get().unreadCount;
    set({ notifications: [], unreadCount: 0 });
    try {
      await apiClient.post("/api/notifications/read-all");
    } catch {
      // Revert on failure — never into another account's bell.
      if (generation !== resetGeneration) return;
      set({ notifications: prev, unreadCount: prevCount });
    }
  },

  addNotification: (notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + 1,
    }));
  },

  subscribeToRealtime: (userId: string) => {
    const generation = resetGeneration;
    const channel = supabase
      .channel(uniqueChannelTopic(`notifications-${userId}`))
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          // The channel is torn down when the bell sees the new user, which is AFTER the reset.
          if (generation !== resetGeneration) return;
          if (newNotification) {
            get().addNotification(newNotification);
          }
        }
      )
      .subscribe();

    // Only this channel: removeChannel() would close every channel in the app (lib/realtime.ts).
    return () => leaveRealtimeChannel(channel);
  },

  reset: () => {
    resetGeneration += 1;
    // `loadedOnce` must fall back too: the bell fetches the list only while it is false
    // (NotificationBell.tsx::handleOpen), so leaving it true showed the previous account's notices
    // to the next one until a page reload.
    set({ notifications: [], unreadCount: 0, loading: false, loadedOnce: false, loadFailed: false });
  },
}));
